import {
  ReactionTypeType as CommentReactions,
  RepliesReactionTypeType as ReplyReactions,
  Type as MarkedLabelType,
  IconTypeType as ReactionIcon,
  QueryV2,
  Category as ApiCategory,
  Role,
} from '@wix/ambassador-comments-v1-category/types';
import { FlowAPI, IHttpClient } from '@wix/yoshi-flow-editor';
import {
  createCategory as apiCreateCategory,
  queryCategory as apiQueryCategory,
  getCategory as apiGetCategory,
  updateCategory as apiUpdateCategory,
} from '@wix/ambassador-comments-v1-category/http';
import { assertFields } from '../../ts-utils';
import { fromServerCategory } from './widget-settings-service.mappers';
import { Category } from './widget-settings-service.types';
import { BLOG_APP_DEF_ID, WIX_COMMENTS_WIDGET_APP_DEF_ID } from '~/constants/app-def-ids';

export const createWidgetSettingsService = ({
  httpClient,
  errorHandler,
  isBlogComments,
}: {
  httpClient: IHttpClient;
  errorHandler: FlowAPI['errorHandler'];
  isBlogComments: boolean;
}) => {
  const appDefId = isBlogComments ? BLOG_APP_DEF_ID : WIX_COMMENTS_WIDGET_APP_DEF_ID;
  const service = {
    createCategory: createCategory(httpClient, appDefId, errorHandler),
    queryCategories: queryCategories(httpClient, appDefId, errorHandler),
    updateCategory: updateCategory(httpClient, errorHandler),
    getCategory: getCategory(httpClient, errorHandler),
  };

  return service;
};

const createCategory =
  (httpClient: IHttpClient, appDefId: string | undefined, errorHandler: FlowAPI['errorHandler']) =>
  async () => {
    const { withErrorHandler, getResolvedError } = errorHandler;

    try {
      const defaults: Partial<ApiCategory> = {
        name: 'Comments',
        guestCommenting: true,
        guestReactions: true,
        reactionType: CommentReactions.REACTIONS,
        repliesReactionType: ReplyReactions.REACTIONS,
        mainReaction: { iconType: ReactionIcon.THUMBS_UP },
        ratingsSettings: { ratingsEnabled: false, ratingRequired: false },
        markedCommentLabelType: MarkedLabelType.FEATURED,
        permissionsSettings: {
          createComment: { role: Role.ALL },
          createReply: { role: Role.ALL },
          createReact: { role: Role.ALL },
        },
      };

      const { data } = await withErrorHandler(
        () => httpClient.request(apiCreateCategory({ category: { ...defaults, appId: appDefId } })),
        { errorCodesMap: { applicationError: { UNAUTHENTICATED: false } } },
      );

      return data.category?.id ?? undefined;
    } catch (error) {
      getResolvedError(error);
      throw error;
    }
  };

const queryCategories = (
  httpClient: IHttpClient,
  appDefId: string | undefined,
  errorHandler: FlowAPI['errorHandler'],
) =>
  async function (request: QueryV2 = {}) {
    const { withErrorHandler, getResolvedError } = errorHandler;

    try {
      const { data } = await withErrorHandler(
        () =>
          httpClient.request(
            apiQueryCategory({
              query: {
                ...request,
                filter: {
                  appId: appDefId,
                },
              },
            }),
          ),
        { errorCodesMap: { applicationError: { UNAUTHENTICATED: false } } },
      );
      assertFields(data, ['categories'], 'apiQueryCategory reponse');

      return data.categories.map(fromServerCategory);
    } catch (error) {
      getResolvedError(error);
      throw error;
    }
  };

const getCategory =
  (httpClient: IHttpClient, errorHandler: FlowAPI['errorHandler']) =>
  async (categoryId: string) => {
    const { withErrorHandler, getResolvedError } = errorHandler;

    try {
      const { data } = await withErrorHandler(
        () => httpClient.request(apiGetCategory({ categoryId })),
        { errorCodesMap: {} },
      );
      assertFields(data, ['category'], 'apiGetCategory reponse');

      return fromServerCategory(data.category);
    } catch (error) {
      getResolvedError(error);
      throw error;
    }
  };

const updateCategory = (httpClient: IHttpClient, errorHandler: FlowAPI['errorHandler']) =>
  async function ({
    currentCategory,
    update,
  }: {
    currentCategory: Category;
    update: Omit<ApiCategory, 'revision' | 'id'>;
  }) {
    const { withErrorHandler, getResolvedError } = errorHandler;

    try {
      //  This API has some quirks that you should be aware of.
      // 1. It requires a 'mask' to be sent with the request, indicating which fields are being updated.
      // any fields not in the mask will be ignored, even if they're present in the request body.
      // 2. Certain fields ('id', 'revision', 'name', 'guestCommenting', 'guestReactions') are always required,
      // even if their values are not being updated.
      const { id, revision, name, guestCommenting, guestReactions } = currentCategory;

      const { data } = await withErrorHandler(
        () =>
          httpClient.request(
            apiUpdateCategory({
              category: {
                ...{ id, revision, name, guestCommenting, guestReactions },
                ...update,
              },
              mask: Object.keys(update),
            }),
          ),
        { errorCodesMap: {} },
      );
      assertFields(data, ['category'], 'apiUpdateCategory reponse');

      return fromServerCategory(data.category);
    } catch (error) {
      getResolvedError(error);
      throw error;
    }
  };
